import Dexie, { Table } from 'dexie';
import { BankRequestCacheEntry, LocalBackup } from '@app/domain/entities';
import type { ReportEntity } from '@app/domain/entities';

/** Shape stored in the `backup_data` object store. */
export interface BackupDataRecord {
  backupId: string;
  data: ArrayBuffer;
}

/**
 * Dexie database class with typed tables.
 * Store names are preserved from the previous idb implementation.
 * DB name changed to 'metadata_v2' (clean break from the old idb schema).
 */
export class AppDatabase extends Dexie {
  /** Out-of-line key store; values are arbitrary (ArrayBuffer, string, objects, …). */
  declare keys: Table<unknown, string>;
  declare backups: Table<LocalBackup, string>;
  declare backup_data: Table<BackupDataRecord, string>;
  declare reports: Table<ReportEntity, string>;
  declare bank_request_cache: Table<BankRequestCacheEntry, string>;

  constructor(dbName: string) {
    super(dbName);
    this.version(1).stores({
      keys: '',
      backups: 'id,createdAt',
      backup_data: 'backupId',
      reports: 'id,createdAt',
      bank_request_cache: 'id,fetchedAt,[source+accountId],[source+accountId+fromTimestamp]',
    });
  }
}

type IndexedDbConnectionState = 'idle' | 'opening' | 'open' | 'closing' | 'closed' | 'terminated' | 'failed';

type IndexedDbLifecycleEventType =
  | 'open-requested'
  | 'blocked'
  | 'open-succeeded'
  | 'versionchange'
  | 'closed'
  | 'terminated'
  | 'open-failed'
  | 'connection-reset';

export interface IndexedDbLifecycleEvent {
  type: IndexedDbLifecycleEventType;
  at: number;
  details?: Record<string, unknown>;
}

export interface IndexedDbDiagnostics {
  dbName: string;
  requestedVersion: number;
  activeVersion: number | null;
  connectionState: IndexedDbConnectionState;
  recentEvents: IndexedDbLifecycleEvent[];
  lastCloseReason: IndexedDbLifecycleEvent | null;
}

export class IndexedDbFactory {
  private readonly DB_NAME: string;
  private readonly DB_VERSION: number;
  private dbPromise: Promise<AppDatabase> | null = null;
  private activeDb: AppDatabase | null = null;
  private connectionState: IndexedDbConnectionState = 'idle';
  private readonly lifecycleEvents: IndexedDbLifecycleEvent[] = [];
  private lastCloseReason: IndexedDbLifecycleEvent | null = null;

  constructor(options?: { dbName?: string; dbVersion?: number }) {
    this.DB_NAME = options?.dbName ?? 'metadata_v2';
    this.DB_VERSION = options?.dbVersion ?? 1;
  }

  getDb(): Promise<AppDatabase> {
    if (!this.dbPromise) {
      this.connectionState = 'opening';
      this.recordEvent('open-requested', { requestedVersion: this.DB_VERSION });

      const database = new AppDatabase(this.DB_NAME);
      this.attachLifecycleListeners(database);

      this.dbPromise = database
        .open()
        .then(() => {
          this.activeDb = database;
          this.connectionState = 'open';
          this.recordEvent('open-succeeded', {
            activeVersion: database.verno,
            objectStores: database.tables.map((t) => t.name),
          });
          return database;
        })
        .catch((error: unknown) => {
          this.connectionState = 'failed';
          this.recordEvent('open-failed', this.describeError(error));
          this.resetCachedConnection();
          throw error;
        });
    }

    return this.dbPromise;
  }

  getDiagnostics(): IndexedDbDiagnostics {
    return {
      dbName: this.DB_NAME,
      requestedVersion: this.DB_VERSION,
      activeVersion: this.activeDb?.verno ?? null,
      connectionState: this.connectionState,
      recentEvents: [...this.lifecycleEvents],
      lastCloseReason: this.lastCloseReason,
    };
  }

  noteOperationFailure(error: unknown): void {
    if (!this.isClosingConnectionError(error)) {
      return;
    }

    this.connectionState = this.connectionState === 'open' ? 'closing' : this.connectionState;
    this.recordEvent('connection-reset', this.describeError(error));
    this.resetCachedConnection();
  }

  private attachLifecycleListeners(database: AppDatabase): void {
    database.on('blocked', () => {
      this.recordEvent('blocked', {});
      console.warn('[IndexedDbFactory] Opening IndexedDB was blocked by another connection', {
        dbName: this.DB_NAME,
      });
    });

    database.on('versionchange', (event: IDBVersionChangeEvent) => {
      this.connectionState = 'closing';

      const details = {
        oldVersion: event.oldVersion,
        newVersion: event.newVersion,
        reason: event.newVersion === null ? 'delete-requested' : 'upgrade-requested',
      };

      const lifecycleEvent = this.recordEvent('versionchange', details);
      this.lastCloseReason = lifecycleEvent;

      console.warn('[IndexedDbFactory] IndexedDB connection is closing after versionchange', {
        dbName: this.DB_NAME,
        ...details,
      });

      this.resetCachedConnection(database);
      database.close();
      this.connectionState = 'closed';
      this.recordEvent('closed', details);
    });

    database.on('close', () => {
      if (this.activeDb === database) {
        this.resetCachedConnection(database);
      }

      // Only treat as unexpected termination when the connection was genuinely open.
      // During a planned versionchange, connectionState is already 'closing', so we
      // skip this path to avoid overwriting the versionchange lastCloseReason.
      if (this.connectionState === 'open') {
        this.connectionState = 'terminated';
        const lifecycleEvent = this.recordEvent('terminated', {
          message: 'Database connection closed unexpectedly',
        });
        this.lastCloseReason = lifecycleEvent;

        console.warn('[IndexedDbFactory] IndexedDB connection terminated unexpectedly', {
          dbName: this.DB_NAME,
        });
      }
    });
  }

  private resetCachedConnection(database?: AppDatabase): void {
    if (database && this.activeDb !== database) {
      return;
    }

    this.activeDb = null;
    this.dbPromise = null;
  }

  private recordEvent(type: IndexedDbLifecycleEventType, details?: Record<string, unknown>): IndexedDbLifecycleEvent {
    const event: IndexedDbLifecycleEvent = {
      type,
      at: Date.now(),
    };

    if (details !== undefined) {
      event.details = details;
    }

    this.lifecycleEvents.push(event);
    if (this.lifecycleEvents.length > 10) {
      this.lifecycleEvents.shift();
    }

    return event;
  }

  private describeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        errorName: error.name,
        errorMessage: error.message,
      };
    }

    return {
      errorName: typeof error,
      errorMessage: String(error),
    };
  }

  private isClosingConnectionError(error: unknown): boolean {
    return (
      error instanceof DOMException &&
      error.name === 'InvalidStateError' &&
      error.message.includes('database connection is closing')
    );
  }
}
